import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import type { ManualBody, ManualBodyStep, ManualMediaRef } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DeferredActionButton } from "@/components/ui/deferred-action-button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import type { useManualBodyMutations } from "../useManualMutations.js";
import type { BodyWriteGate } from "./useBodyWriteGate.js";
import { STEP_TYPE_ICON } from "./step-type-icon.js";
import type { ReactNode } from "react";
import { PayloadSection, usePayloadDraft } from "./StepPayloadEditor.js";
import { Disclosure } from "@/components/ui/disclosure";
import { SETTING_FILE_TYPES } from "./setting-file-types.js";
import { fileMatches, useSideDrop, type DroppedFile } from "./side-drag.js";
import { SECTION_LABELS, isInlineSetting, sectionsForStep, useBodySections, type BodySectionKey } from "./body-sections.js";
import { bindElementBody, StepArElementBody, StepArEmptyBody, StepArStrip } from "./StepArBlock.js";
import { StepMedia } from "./StepMedia.js";
import { StepJobBadge } from "./StepJobBadge.js";


type Mutations = ReturnType<typeof useManualBodyMutations>;
type StateField = "standard" | "completed" | "failed";
const STATE_FIELDS: StateField[] = ["standard", "completed", "failed"];

/**
 * One step's content editor (ADR-002 Phase 10 — the content subset). Mirrors the legacy step CARD: an editable name, a
 * type picker, the standard / completed / failed instruction text (the shipped {@link RichTextEditor}, sanitized on write),
 * and — for interactive types — the typed payload.
 *
 * **Text autosave (panel-hardened).** Each state is tracked against its last-SAVED value; a debounced flush sends ONLY the
 * fields the author actually changed (never re-ships an untouched state), and CASes against the version the edit session
 * STARTED on (`baseVersion`) — so a concurrent edit that bumped `manual.version` fails the CAS (→ 409 banner) instead of
 * silently overwriting the other author (panel Critical). Untouched fields re-sync from incoming props on a refetch. The
 * card does NOT self-flush on unmount; the parent flushes all pending saves at the real boundaries (section switch, exit,
 * status transition) and DISCARDS drafts on a conflict-refresh — see {@link ManualEditor}. The AR block ({@link StepArBlock},
 * AR phase 2) renders the step's element strip and its READ-ONLY placement for an AR manual; the pickers behind it are
 * still deferred placeholders so the layout stays honest.
 */
/** The sections that are what the step is about. The rest (the texts after completing or failing, descriptions) stays collapsed. */
const ALWAYS_OPEN_SECTIONS = new Set<BodySectionKey>(["standard", "question", "answers", "checklistAnswers"]);

export function StepCard({
  step,
  gate,
  disabled,
  m,
  onPickMain,
  onAddElement,
  onRemoveElement,
  onDuplicateElement,
  onSetSetting,
  onPickSettingFile,
  copiedFrom,
  onCopySettings,
  onPasteSettings,
  isArManual = true,
  registerFlush,
  unregisterFlush,
  isRefreshing,
  job,
  onPickMedia,
  onDetachMedia,
  renderMenu,
  autoFocusName = false,
  current = true,
  onNameFocused,
  session,
  manualId = null,
  initialStateSensorIds,
  onElementSelected,
}: {
  step: ManualBodyStep;
  /** The author picked an element of this step (its tab, or the copy a duplicate selects) — the 3D test frames it in Unity. */
  onElementSelected?: (elementId: number) => void;
  /** The sensors THIS step is the first in the manual to use (ADR-037 D7). Computed once by the editor, because the
   *  answer needs the whole body and a card only needs its own share of it. */
  initialStateSensorIds?: ReadonlySet<number>;
  /** The editor-wide serialized write gate — supplies the CAS version for each write + serializes them so a sibling card's
   *  save can't stale this card, and rejects on error so the flush barrier blocks a transition/exit (panel #210). */
  gate: BodyWriteGate;
  disabled: boolean;
  m: Mutations;
  /** Open the asset library (ADR-024) to pick media for THIS step. When provided (and editable), the Add/Replace-media
   *  button becomes live; absent ⇒ the deferred placeholder. The attach itself lands with the RED-ZONE 11a-4 write. */
  onPickMedia?: () => void;
  /** Remove the step's media (ADR-027 §9 — the legacy tile's ×). */
  onDetachMedia?: () => void;
  /** The step's actions menu in the card's head; gets the function that puts the cursor into this card's name field. */
  renderMenu?: (focusName: () => void) => ReactNode;
  /** Opened by "Umbenennen": focus the name once the menu has closed, then report back so a later open does not. */
  autoFocusName?: boolean;
  /** The step you are on. With every step open (a 2D-only manual) only that card carries the brand frame. */
  current?: boolean;
  onNameFocused?: () => void;
  /** Open the main-element picker for this step. Receives the step id and its CURRENT blueprint so the drawer can mark
   *  the active tile. Absent (or read-only) ⇒ the deferred placeholder. */
  onPickMain?: (stepId: number) => void;
  /** Open the `+` element picker for this step. */
  onAddElement?: (stepId: number) => void;
  /** Remove one of the step's non-main elements. */
  onRemoveElement?: (stepId: number, elementId: number) => void;
  /** Deep-copy a non-main element onto this step. Resolves with the fresh body so the card can select the copy's tab
   *  (the legacy's `SelectARElementTab(newIndex)`); `void` when the write was not performed. */
  onDuplicateElement?: (stepId: number, elementId: number) => Promise<ManualBody | void>;
  /** Write one element setting's scalar value (ADR-027 Phase B). Absent ⇒ settings stay read-only. */
  onSetSetting?: (stepId: number, elementId: number, name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
  /** Open the asset library to attach a file to a file-valued setting. */
  onPickSettingFile?: (stepId: number, elementId: number, name: string, mode: "add" | "replace", currentFileIds: number[], dropped?: DroppedFile) => void;
  /** The settings clipboard (legacy `Einstellungen kopieren/einfügen`). */
  copiedFrom?: { elementId: number; label: string; settingNames: string[] } | null;
  /** Copy carries the source's SETTING NAMES so a Paste is only ever offered where at least one is shared. */
  onCopySettings?: (elementId: number, label: string, settingNames: string[]) => void;
  onPasteSettings?: (stepId: number, elementId: number) => void;
  /** This manual is an AR manual. Governs BOTH the Type picker and the AR block below: only an AR manual has a
   *  main-element type in the legacy, and only an AR manual has elements at all — a non-AR / document manual (699 of
   *  784 Takeda manuals) is text-only, so both are hidden there (legacy parity). Named for the fact, not the widget,
   *  because it now drives two of them. */
  isArManual?: boolean;
  /** Register/unregister this card's flush (returns a Promise) so the parent can AWAIT pending saves before a boundary. */
  registerFlush?: (fn: () => Promise<void>) => void;
  unregisterFlush?: (fn: () => Promise<void>) => void;
  /** True while a conflict-refresh is deliberately discarding local drafts — suppresses the unmount flush (panel r2). */
  isRefreshing?: { current: boolean };
  /** This step's newest background media job, if any — drives the queued/processing/finished badge (ADR-026 Phase 6). */
  job?: import("@arc/contracts").MediaJob;
  /** The auth session — threaded to {@link StepMedia} so its t("manualEditor.addMoreImages") (collage) can open the asset picker. Optional
   *  so the unit tests that render a bare StepCard don't need to supply one (the collage affordance stays deferred then). */
  session?: AuthSession | null;
  /** The manual in context (#217 P2) — threaded on to the collage picker so its asset drawer offers the "This manual" scope. */
  manualId?: number | null;
}) {
  const { t } = useTranslation();
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const [name, setName] = useState(step.name);
  // Which element's body is showing (ADR-027). `null` = follow the MAIN element, which is what a freshly rendered
  // card shows — and it self-heals when the selected element is removed, since the lookup misses and falls back.
  const [selectedElementId, setSelectedElementId] = useState<number | null>(null);
  const selectElement = (elementId: number) => {
    setSelectedElementId(elementId);
    onElementSelected?.(elementId);
  };
  useEffect(() => setName(step.name), [step.name]);
  // A newly added element is selected at once (owner 2026-11-04): its tab is active and its settings show. Only while the
  // card is mounted, so opening a step never jumps away from its main element; a new MAIN element (a type change) is
  // followed anyway, as `null` means "the main one".
  const knownElementIds = useRef(new Set(step.elements.map((e) => e.id)));
  const selectRef = useRef(selectElement);
  selectRef.current = selectElement;
  useEffect(() => {
    const added = step.elements.filter((e) => !knownElementIds.current.has(e.id) && !e.isMain);
    knownElementIds.current = new Set(step.elements.map((e) => e.id));
    if (added.length === 1) selectRef.current(added[0]!.id);
  }, [step.elements]);
  // The body's SECTIONS (ADR-027 §11, owner "pure legacy"): the legacy per-type list + the content fallback, each a
  // collapsible that starts closed and is remembered per element + section; "Alle ausklappen" flips them all.
  const sectionKeys = useMemo(() => sectionsForStep(step), [step]);
  // ONE state for the main element: its body sections + its remaining settings (the element body's list), so a single
  // "Alle ausklappen" covers the whole element, as in the legacy. Keyed by the main element id when it exists.
  const mainForSections = step.elements.find((e) => e.isMain) ?? null;
  const canSetSetting = onSetSetting != null;
  const allKeys = useMemo(
    // Only what has a collapsible: "Alle ausklappen" must not count what is always open, or its label would lie.
    () => [
      ...sectionKeys.filter((k) => !ALWAYS_OPEN_SECTIONS.has(k)),
      ...(mainForSections?.settings.map((x) => x.name).filter((n) => !["Text", "Image", "Video"].includes(n) && !isInlineSetting(n, canSetSetting)) ?? []),
    ],
    [sectionKeys, mainForSections, canSetSetting],
  );
  const sections = useBodySections(mainForSections?.id ?? step.id, allKeys);

  const { enqueue, markDirty, markClean } = gate;
  // `buffers` = the live edited HTML per state; `lastSaved` = the value last persisted (or the server value). A field is
  // DIRTY iff they differ; the shared {@link useBodyWriteGate} owns the CAS version (no per-card base version anymore).
  const buffers = useRef<Record<StateField, string | null>>({ standard: step.text, completed: step.completedText, failed: step.failedText });
  const lastSaved = useRef<Record<StateField, string | null>>({ standard: step.text, completed: step.completedText, failed: step.failedText });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The in-flight save + the buffers snapshot it sent — so a re-invoked flush (a structural op awaiting it, or a debounce
  // firing mid-save) REUSES the pending save when nothing changed instead of enqueuing a duplicate (panel diff-#210 Medium).
  const activeFlush = useRef<Promise<void> | null>(null);
  const activeBuffers = useRef<Record<StateField, string | null> | null>(null);

  const isClean = () => STATE_FIELDS.every((f) => buffers.current[f] === lastSaved.current[f]);
  // Per-field remount key — bumped when the SERVER value of a CLEAN field changes, so the (uncontrolled) TipTap editor
  // REMOUNTS to display the new content (a background refetch / concurrent author's save). A DIRTY field stays mounted (its
  // base-version CAS guards it); without this the user would keep editing stale-displayed text against the fresh version and
  // silently clobber the other author (panel r2 Critical).
  const [mountKeys, setMountKeys] = useState<Record<StateField, number>>({ standard: 0, completed: 0, failed: 0 });
  const mainElement = step.elements.find((e) => e.isMain) ?? null;
  const selectedElement = step.elements.find((e) => e.id === selectedElementId) ?? mainElement;
  /** The step's instruction/payload/media hang off the MAIN element, so they show on its tab (and on any non-AR step). */
  const showMainContent = !isArManual || selectedElement == null || selectedElement.isMain;
  useEffect(() => {
    const next: Record<StateField, string | null> = { standard: step.text, completed: step.completedText, failed: step.failedText };
    setMountKeys((prev) => {
      let changed = false;
      const out = { ...prev };
      STATE_FIELDS.forEach((f) => {
        if (buffers.current[f] === lastSaved.current[f] && lastSaved.current[f] !== next[f]) {
          buffers.current[f] = next[f];
          lastSaved.current[f] = next[f];
          out[f] = prev[f] + 1;
          changed = true;
        }
      });
      return changed ? out : prev;
    });
  }, [step.text, step.completedText, step.failedText]);

  // A debounced flush routed through the shared gate: it RESOLVES only when the save actually SUCCEEDS and REJECTS on error,
  // so the parent's flush barrier can block a transition/exit on a conflict instead of proceeding with an unsaved edit
  // (panel #210 High #2). The gate supplies the CAS version left by the prior write, so a sibling card's save can't stale us
  // (panel #210 High #1). A no-change flush is a resolved no-op.
  const flush = (): Promise<void> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const changed = STATE_FIELDS.filter((f) => buffers.current[f] !== lastSaved.current[f]);
    if (changed.length === 0) {
      // No-op (reverted to saved / nothing dirty): clear this card's dirtiness so the shared version can reconcile again —
      // otherwise a reverted edit would freeze the CAS token and 409 the user's OWN next write (panel diff-#210 High #2).
      markClean(step.id);
      return activeFlush.current ?? Promise.resolve();
    }
    // A save is already in flight for the SAME buffers — reuse it (a structural op awaiting the flush, or the debounce firing
    // mid-save) instead of enqueuing a duplicate write + spurious version bump (panel diff-#210 Medium).
    if (activeFlush.current && activeBuffers.current && STATE_FIELDS.every((f) => buffers.current[f] === activeBuffers.current![f])) {
      return activeFlush.current;
    }
    const patch: Partial<Record<StateField, string | null>> = {};
    const sent: Partial<Record<StateField, string | null>> = {};
    changed.forEach((f) => {
      patch[f] = buffers.current[f];
      sent[f] = buffers.current[f];
    });
    const snapshot: Record<StateField, string | null> = { ...buffers.current };
    const p = enqueue(m.editStepText, (expectedVersion) => ({ stepId: step.id, expectedVersion, ...patch })).then(() => {
      // Mark exactly what we sent as saved; a keystroke that arrived mid-flight leaves that field dirty for the next flush.
      (Object.keys(sent) as StateField[]).forEach((f) => (lastSaved.current[f] = sent[f]!));
      if (isClean()) {
        markClean(step.id); // fully back in sync → the shared version can reconcile again
      } else if (!mounted.current) {
        // The card is GONE (a view switch / exit already awaited the barrier, or a refresh discarded it): re-dirtying the
        // gate here would add an id no card can ever clear again, and the barrier's dirtiness check would stay red until
        // a reload (gate-panel round-2 High). The unmount flush already carried the final buffers.
      } else {
        // A revert/keystroke landed DURING this in-flight save, so the live buffers now differ from what we just persisted.
        // Without re-arming, the SERVER keeps the mid-flight value while the UI shows the newer one, and — because the earlier
        // no-op flush already markClean'd + cleared the timer — the gate goes quiescent (dirty empty, pending empty, no timer)
        // so `settle()` resolves GREEN with no banner: silent loss (panel diff-f5 High). Re-dirty + reschedule to flush the
        // final state (a lifecycle boundary also force-flushes first via flushAll, then blocks on the resulting write).
        markDirty(step.id);
        schedule();
      }
    });
    activeFlush.current = p;
    activeBuffers.current = snapshot;
    void p
      .finally(() => {
        if (activeFlush.current === p) {
          activeFlush.current = null;
          activeBuffers.current = null;
        }
      })
      .catch(() => {});
    return p;
  };
  const schedule = () => {
    if (timer.current) clearTimeout(timer.current);
    // An autosave failure surfaces via the mutation's error state (the editor's Refresh banner) — swallow here so the
    // debounce timer never raises an unhandled rejection.
    timer.current = setTimeout(() => void flush().catch(() => {}), 700);
  };
  const edit = (field: StateField, html: string) => {
    const wasClean = isClean();
    buffers.current[field] = html;
    // First field to diverge → mark this card dirty so the gate FREEZES the shared version (a concurrent cross-user edit
    // then still 409s instead of being silently clobbered). Reverting the last dirty field back to saved → clean again, so the
    // version can reconcile without waiting for the debounced no-op flush (panel diff-#210 High #2).
    if (wasClean && buffers.current[field] !== lastSaved.current[field]) markDirty(step.id);
    else if (!wasClean && isClean()) markClean(step.id);
    schedule();
  };

  // Register the flush with the parent (awaited before a transition), clear the timer on unmount, AND flush a pending edit on
  // unmount (browser Back / cross-section move / editor close) — UNLESS a conflict-refresh is deliberately discarding (panel r2).
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const mounted = useRef(true);
  useEffect(() => {
    // Text AND payload: a lifecycle boundary (exit / section switch / status transition) awaits both.
    const fn = () => Promise.all([flushRef.current(), payloadFlush.current()]).then(() => undefined);
    registerFlush?.(fn);
    mounted.current = true;
    return () => {
      mounted.current = false;
      unregisterFlush?.(fn);
      if (timer.current) clearTimeout(timer.current);
      markClean(step.id); // this card no longer contributes dirtiness (flushed at a boundary, or its draft discarded on refresh)
      markClean(-step.id);
      // Flush a pending edit on unmount (Back / cross-section move / editor close) UNLESS a conflict-refresh is deliberately
      // discarding drafts. A failure surfaces via the banner — swallow the rejection here.
      if (!isRefreshing?.current) {
        void flushRef.current().catch(() => {});
        void payloadFlush.current().catch(() => {});
      }
    };
  }, [registerFlush, unregisterFlush, isRefreshing, markClean, step.id]);

  // Structural writes SERIALIZE behind the pending text flush through the SAME gate, so a type/rename/payload change can't race
  // the autosave into a self-inflicted 409 (the gate injects the CAS version left by the prior write). `flush()` first enqueues
  // any pending debounced text (order preserved); its error surfaces via the banner, so it's swallowed here. Rename reverts its
  // input on a rejected name (panel editability Medium — no silent false-confirm); type/payload errors show inline.
  const rename = async () => {
    const next = name.trim();
    if (!next || next === step.name) {
      setName(step.name);
      return;
    }
    await flush().catch(() => {});
    enqueue(m.renameStep, (expectedVersion) => ({ stepId: step.id, expectedVersion, name: next })).catch(() => setName(step.name));
  };
  // The interactive payload autosaves like the text (ADR-027 §11 C). Its dirtiness is tracked under `-step.id` — a
  // distinct gate id, so a text save's `markClean(step.id)` can never green-light an unsaved payload at the exit barrier.
  const payloadDraft = usePayloadDraft({
    type: step.type,
    payload: step.payload,
    onSave: async (payload) => {
      await enqueue(m.editStepPayload, (expectedVersion) => ({ stepId: step.id, expectedVersion, payload }));
    },
    onDirty: () => markDirty(-step.id),
    onClean: () => markClean(-step.id),
  });
  const payloadFlush = useRef(payloadDraft.flush);
  payloadFlush.current = payloadDraft.flush;

  /* ADR-027 §11 (owner: pure legacy): the main element's body is a stack of COLLAPSIBLE sections in the legacy order
     and with the legacy labels; all start closed, each remembers its state per element; the element body's toggle
     ("Alle ausklappen / einklappen") flips them together with the element's remaining settings. */
  const renderSection = (key: BodySectionKey) => (
              key === "standard" ? (
                /* Editor LEFT, media RIGHT (owner, ADR-027 §9) — takeda's own main layout is called `Hauptelement
                   Text links`. They stack on a narrow viewport. Without media the text takes the width: an empty tile
                   beside it read as a hole (owner 2026-11-02). */
                <div className={cn("grid grid-cols-[minmax(0,1fr)] gap-4", step.media && "@3xl:grid-cols-[minmax(0,1fr)_22rem]")}>
                  <StateEditor key={`standard-${mountKeys.standard}`} label={t("manualEditor.instruction")} value={step.text ?? ""} disabled={disabled} onChange={(html) => edit("standard", html)} />
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-muted-foreground text-xs font-medium">{t("manualEditor.media")}</span>
                  {step.media ? (
                    /* As tall as the text beside it (owner 2026-11-03): the label rows match, the tile fills the rest. */
                    <div className="relative flex min-h-[9.5rem] flex-1 flex-col [&>*]:flex-1">
                      <StepMedia
                        media={step.media}
                        canEdit={!disabled}
                        session={session}
                        manualId={manualId}
                        thumbClassName="absolute! inset-0 h-full w-full rounded-lg"
                        onSaveEditedImage={
                          disabled
                            ? undefined
                            : (file: File) =>
                                gate.enqueue(m.saveEditedStepImage, (expectedVersion) => ({ stepId: step.id, file, expectedVersion })).catch((err: unknown) => {
                                  // The edit/collage save is a DISCRETE action with its OWN error UI (the editor's banner). Its failure
                                  // must NOT latch the gate's exit-barrier — that latch is for unsaved AUTOSAVE divergence; leaving it set
                                  // silently wedges the manual editor's exit/status-transition after the author cancels (panel collage F2).
                                  // Acknowledge ONLY this failure's latch (diff-panel R3-r4-#1) — NOT `reseed()`, which clears the whole
                                  // dirty-set + version and would let another card's unsaved text absorb a concurrent cross-user version
                                  // (silent lost update). Then re-throw for the editor to surface + allow retry.
                                  gate.ackFailure();
                                  throw err;
                                })
                        }
                        onCutVideo={
                          // The video CUT is ASYNC + carries NO version CAS (unlike the body-write gate above) → call the plain mutation
                          // directly; on the 202 the worker trims + the API lands it on the step later (ADR-025 A4).
                          disabled ? undefined : (input) => m.cutStepVideo.mutateAsync({ stepId: step.id, ...input })
                        }
                      />
                      {/* No t("manualEditor.replaceMedia") button (owner, §9). The thumbnail's own click stays the PREVIEW — that is
                          where Edit image / Add more images / Cut video live (ADR-025) — so "replace" is a small named
                          control on the tile's corner: one click, visible on hover, always reachable by keyboard. */}
                      {!disabled && (onPickMedia || onDetachMedia) ? (

                        <div className="absolute top-1.5 right-1.5 flex gap-1">

                          {onPickMedia ? (

                            <Button variant="outline" size="icon-sm" aria-label={t("manualEditor.replaceMedia")} onPress={onPickMedia} className="bg-background/85 border-border/50 text-foreground hover:bg-background shadow-sm backdrop-blur-sm">

                              <ImagePlus aria-hidden="true" className="size-3.5" />

                            </Button>

                          ) : null}

                          {onDetachMedia ? (

                            /* The legacy tile's × — an EMPTY file list on the setting; the text stays (ADR-027 §9). */

                            <Button variant="outline" size="icon-sm" aria-label={t("manualEditor.removeMedia")} onPress={onDetachMedia} className="bg-background/85 border-border/50 text-foreground hover:bg-background shadow-sm backdrop-blur-sm">

                              <X aria-hidden="true" className="size-3.5" />

                            </Button>

                          ) : null}

                        </div>

                      ) : null}
                    </div>
                  ) : onPickMedia && !disabled ? (
                    /* The empty state IS the click target — a dashed tile, like the empty main-element tile. */
                    <button
                      type="button"
                      onClick={onPickMedia}
                      aria-label={t("manualEditor.addMedia")}
                      className="border-border bg-transparent text-muted-foreground hover:border-primary hover:text-primary focus-visible:ring-primary inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-md border border-dashed px-3 py-1.5 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none"
                    >
                      <ImagePlus aria-hidden="true" className="size-3.5" />
                      {t("manualEditor.addMedia")}
                    </button>
                  ) : (
                    <DeferredActionButton
                      label={step.media ? t("manualEditor.replaceMedia") : t("manualEditor.addMedia")}
                      tooltip={t("manualEditor.mediaLibrarySoon")}
                      variant="outline"
                      className="dark:border-border"
                      icon={<ImagePlus aria-hidden="true" className="size-3.5" />}
                    />
                  )}
                  {/* Background media work for this step (ADR-026 Phase 6) — under the media so the state is read where
                      the result will appear, not in a distant corner of the page. */}
                  {job ? <StepJobBadge job={job} attachedFileId={step.media?.fileId ?? null} /> : null}
                </div>
                </div>
              ) : key === "completed" || key === "failed" ? (
                /* Text LEFT, the state's own image/video RIGHT (ADR-027 §11 B) — the legacy's "Abgeschlossen / Fehlgeschlagen
                   Text und Bild/Video": the file attached to that state's text setting. */
                <div className="grid grid-cols-[minmax(0,1fr)] gap-4 @2xl:grid-cols-[minmax(0,1fr)_15rem]">
                  {key === "completed" ? (
                    <StateEditor key={`completed-${mountKeys.completed}`} label={t("manualEditor.completed")} value={step.completedText ?? ""} disabled={disabled} onChange={(html) => edit("completed", html)} />
                  ) : (
                    <StateEditor key={`failed-${mountKeys.failed}`} label={t("manualEditor.failed")} value={step.failedText ?? ""} disabled={disabled} onChange={(html) => edit("failed", html)} />
                  )}
                  <StateMediaColumn
                    media={key === "completed" ? step.completedMedia : step.failedMedia}
                    label={key === "completed" ? t("manualEditor.completedMedia") : t("manualEditor.failedMedia")}
                    session={session}
                    manualId={manualId}
                    onRemove={

                      onSetSetting && !disabled && mainForSections && (key === "completed" ? step.completedMedia : step.failedMedia)

                        ? () => void onSetSetting(step.id, mainForSections.id, key === "completed" ? "Interaction_CompletedText" : "Interaction_FailedText", { fileIds: [] })

                        : undefined

                    }
                    onPick={
                      onPickSettingFile && !disabled && mainForSections
                        ? () => {
                            const cur = key === "completed" ? step.completedMedia : step.failedMedia;
                            onPickSettingFile(step.id, mainForSections.id, key === "completed" ? "Interaction_CompletedText" : "Interaction_FailedText", "replace", cur ? [cur.fileId] : []);
                          }
                        : undefined
                    }
                  />
                </div>
              ) : payloadDraft.draft ? (
                <PayloadSection section={key} draft={payloadDraft.draft} disabled={disabled} onChange={payloadDraft.update} />
              ) : null
  );
  const openKeys = sectionKeys.filter((key) => ALWAYS_OPEN_SECTIONS.has(key));
  const collapsedKeys = sectionKeys.filter((key) => !ALWAYS_OPEN_SECTIONS.has(key));
  const bodySections = showMainContent ? (
    <div className="flex flex-col gap-3" data-testid="body-sections">
      {/* What a step is about is open (owner 2026-11-02); it was one more click on every single step. */}
      {openKeys.map((key) => (
        /* No visible heading: the instruction and every payload editor carry their own field label, and a second line
           above it ("Checkboxen" over "Checklisten-Einträge") said the same thing twice. Lists stay readable in width. */
        <section key={key} aria-label={t(SECTION_LABELS[key])} className={cn("flex flex-col gap-1.5", key !== "standard" && "max-w-3xl")}>
          {renderSection(key)}
        </section>
      ))}
      {collapsedKeys.map((key) => (
        <Disclosure key={key} className="border-0" title={t(SECTION_LABELS[key])} isExpanded={sections.isOpen(key)} onExpandedChange={(open) => sections.setOpen(key, open)}>
          {key === "completed" || key === "failed" ? (
            <StateDropArea
              name={key === "completed" ? "Interaction_CompletedText" : "Interaction_FailedText"}
              enabled={onPickSettingFile != null && !disabled && mainForSections != null}
              onDrop={(name, file) => {
                const cur = key === "completed" ? step.completedMedia : step.failedMedia;
                if (mainForSections) onPickSettingFile?.(step.id, mainForSections.id, name, "replace", cur ? [cur.fileId] : [], file);
              }}
            >
              {renderSection(key)}
            </StateDropArea>
          ) : (
            <div className="p-3">{renderSection(key)}</div>
          )}
        </Disclosure>
      ))}
    </div>
  ) : null;

  const TypeIcon = STEP_TYPE_ICON[step.type];
  const nameFieldId = `step-name-${String(step.id)}`;
  // After the menu has closed and handed focus back to its button.
  const focusName = () => {
    window.setTimeout(() => {
      const field = document.getElementById(nameFieldId);
      if (field instanceof HTMLInputElement) {
        field.focus();
        field.select();
      }
    }, 50);
  };
  const focusNameRef = useRef(focusName);
  focusNameRef.current = focusName;
  const onNameFocusedRef = useRef(onNameFocused);
  onNameFocusedRef.current = onNameFocused;
  useEffect(() => {
    if (!autoFocusName) return;
    focusNameRef.current();
    onNameFocusedRef.current?.();
  }, [autoFocusName]);
  return (
    <section
      className={cn(
        // White on the grey band, as in the facility selection; the open step lifts (dark: one surface up).
        "@container bg-background animate-in fade-in-0 slide-in-from-top-1 rounded-xl border transition-[border-color,box-shadow] duration-200 motion-reduce:animate-none",
        current ? "border-primary/40 ring-primary/10 dark:bg-card shadow-md ring-1 dark:border-primary/50 dark:shadow-lg dark:shadow-black/30" : "border-border/70 shadow-xs",
      )}
      aria-label={t("manualEditor.stepName2", { name: step.name })}
    >
      {/* Header — the step type and its name, which reads as text until you point at it: you rarely change it (owner 2026-11-02).
          Its own tint makes the card less flat (owner 2026-11-03). */}
      <div className="border-border/60 bg-card/60 flex flex-wrap items-center gap-2 rounded-t-xl border-b px-4 py-2 dark:bg-black/20">
        <TypeIcon aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
        <Input
          aria-label={t("manualEditor.stepName")}
          value={name}
          disabled={disabled}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => void rename()}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") setName(step.name);
          }}
          id={nameFieldId}
          className="hover:border-border dark:hover:bg-input/30 h-8 max-w-sm min-w-40 flex-1 border-transparent bg-transparent px-1.5 text-base font-semibold shadow-none dark:bg-transparent"
        />
        {renderMenu ? (
          <div className="ml-auto">
            {renderMenu(focusName)}
          </div>
        ) : null}
      </div>
      {/* One mutation for every card: with all steps open, only the card whose rename failed says so. */}
      {m.renameStep.isError && m.renameStep.variables?.stepId === step.id ? (
        <p className="text-destructive border-border border-b px-4 py-1.5 text-xs">{t("manualEditor.nameInUse")}</p>
      ) : null}

      {/* The element strip first, in the DOM and on screen: "pick the element, then its body" (owner 2026-11-02). */}
      <div className="flex flex-col gap-4 p-4">
        {isArManual ? (
          <StepArStrip
            actionsSlotRef={setActionsSlot}
            elements={step.elements}
            disabled={disabled}
            selectedId={selectedElement?.id ?? null}
            onSelect={selectElement}
            onAddElement={onAddElement ? () => onAddElement(step.id) : undefined}
            busy={
              (m.addStepElement.isPending && m.addStepElement.variables?.stepId === step.id) ||
              (m.removeStepElement.isPending && m.removeStepElement.variables?.stepId === step.id) ||
              (m.duplicateStepElement.isPending && m.duplicateStepElement.variables?.stepId === step.id)
            }
          />
        ) : null}
        <div className="flex flex-col gap-4">
        {/* The selected element's body: its tile, its ALWAYS-visible geometry, its clipboard, its settings — and on
            the main tab the step's own instruction + media below. The card IS the element, as in the legacy. */}
        {isArManual ? (
          selectedElement ? (
              <StepArElementBody
                /* KEYED BY ELEMENT: without it React reuses one instance across tab switches, and a control keyed only
                   by setting NAME reconciles element B's setting onto element A's control — so A's in-flight draft
                   showed over B and the next whole-object write serialised A's value into B (diff-panel R2 High #2). */
                key={selectedElement.id}
                actionsSlot={actionsSlot}
                element={selectedElement}
                siblings={step.elements}
                disabled={disabled}
                initialStateSensorIds={initialStateSensorIds}
                onPickMain={onPickMain ? () => onPickMain(step.id) : undefined}
                onDelete={onRemoveElement ? () => onRemoveElement(step.id, selectedElement.id) : undefined}
                busySetting={
                  (m.addStepElement.isPending && m.addStepElement.variables?.stepId === step.id) ||
                  (m.removeStepElement.isPending && m.removeStepElement.variables?.stepId === step.id) ||
                  (m.duplicateStepElement.isPending && m.duplicateStepElement.variables?.stepId === step.id) ||
                  (m.setElementSetting.isPending && m.setElementSetting.variables?.elementId === selectedElement.id) ||
                  (m.copyElementSettings.isPending && m.copyElementSettings.variables?.elementId === selectedElement.id)
                }
                copiedFrom={copiedFrom}
                sections={selectedElement.isMain ? sections : undefined}
                {...bindElementBody({
                  element: selectedElement,
                  siblings: step.elements,
                  handlers: {
                    onSetSetting: onSetSetting ? (elementId, name, patch) => onSetSetting(step.id, elementId, name, patch) : undefined,
                    onPickSettingFile: onPickSettingFile ? (elementId, name, mode, ids, dropped) => onPickSettingFile(step.id, elementId, name, mode, ids, dropped) : undefined,
                    onCopySettings,
                    onPasteSettings: onPasteSettings ? (elementId) => onPasteSettings(step.id, elementId) : undefined,
                    onDuplicate: onDuplicateElement ? (elementId) => onDuplicateElement(step.id, elementId) : undefined,
                  },
                  // The copy is the one element of THIS step that was not here before.
                  siblingsAfter: (body) => body.sections.flatMap((x) => x.steps).find((x) => x.id === step.id)?.elements,
                  onSelect: selectElement,
                })}
              >
                {selectedElement.isMain ? bodySections : null}
              </StepArElementBody>
          ) : (
            // No element at all yet — the dashed tile IS the picker, since that is how an empty step gets its main element.
            <StepArEmptyBody disabled={disabled} onPickMain={onPickMain ? () => onPickMain(step.id) : undefined} />
          )
        ) : null}

        {/* These belong with the TAB BAR, not with the main element's content (diff-panel R1 consensus). The controls
            that raise them — `+ Add element`, each chip's remove, and `Choose` — are in the always-visible tab bar, so
            nesting the banners inside `showMainContent` meant a failure raised from a non-main tab rendered nowhere and
            the operation just looked like it had done nothing.
            Scoped to the acting step (diff-panel): `m` is ONE manual-wide mutation object shared by every card, so an
            unscoped `isError` lights the banner on all of them and blames every step for one step's failure. */}
        {m.setStepMainElement.isError && m.setStepMainElement.variables?.stepId === step.id ? (
          <p className="text-destructive text-xs">{t("manualEditor.elementApplyFailed")}</p>
        ) : null}

        {m.addStepElement.isError && m.addStepElement.variables?.stepId === step.id ? (
          <p className="text-destructive text-xs">{t("manualEditor.elementAddFailed")}</p>
        ) : null}
        {m.removeStepElement.isError && m.removeStepElement.variables?.stepId === step.id ? (
          <p className="text-destructive text-xs">{t("manualEditor.elementRemoveFailed")}</p>
        ) : null}
        {m.setElementSetting.isError && m.setElementSetting.variables?.stepId === step.id ? (
          <p className="text-destructive text-xs">{t("manualEditor.settingSaveFailed")}</p>
        ) : null}
        {/* The paste had NO banner of its own, so a refused paste looked like nothing had happened (diff-panel R3 High #1). */}
        {m.copyElementSettings.isError && m.copyElementSettings.variables?.stepId === step.id ? (
          <p className="text-destructive text-xs">{t("manualEditor.pasteFailed")}</p>
        ) : null}
        {m.duplicateStepElement.isError && m.duplicateStepElement.variables?.stepId === step.id ? (
          <p className="text-destructive text-xs">{t("manualEditor.duplicateFailed")}</p>
        ) : null}

        {/* The step's instruction, payload and media all live on the MAIN element, so they belong to its tab. Showing
            them under an arrow's tab would read as if the arrow carried the step's text (owner mockups: ANWEISUNG and
            MEDIEN appear only under HAUPTELEMENT). */}
        {/* The sections render INSIDE the main element's body (under the element's own toggle) when that body is on
            screen; otherwise — a non-AR manual, or an AR step with no main element yet — they stand alone with a
            toggle of their own. */}
        {!(isArManual && selectedElement?.isMain) && bodySections ? bodySections : null}
        </div>
      </div>
    </section>
  );
}

/**
 * The completed / failed state's own image or video (ADR-027 §11 B): a preview (no edit-image / cut-video — those act
 * on the STEP's standard media) with the same replace-corner / dashed-add affordances as the standard tile. Picking
 * goes through the element-setting file picker on the state's text setting.
 */
function StateMediaColumn({
  media,
  label,
  session,
  manualId,
  onPick,
  onRemove,
}: {
  media: ManualMediaRef | null;
  label: string;
  session?: AuthSession | null;
  manualId?: number | null;
  onPick?: () => void;
  /** The legacy tile's × — an EMPTY file list on the state's setting. */
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-muted-foreground text-xs font-medium">{t("manualEditor.media")}</span>
      {media ? (
        <div className="relative">
          <StepMedia media={media} canEdit={false} session={session} manualId={manualId} thumbClassName="h-36 w-full" />
          {onPick || onRemove ? (
            <div className="absolute top-1.5 right-1.5 flex gap-1">
              {onPick ? (
                <Button variant="outline" size="icon-sm" aria-label={t("manualEditor.replaceTolowercase", { toLowerCase: label.toLowerCase() })} onPress={onPick} className="bg-background/85 border-border/50 text-foreground hover:bg-background shadow-sm backdrop-blur-sm">
                  <ImagePlus aria-hidden="true" className="size-3.5" />
                </Button>
              ) : null}
              {onRemove ? (
                <Button variant="outline" size="icon-sm" aria-label={t("manualEditor.removeTolowercase", { toLowerCase: label.toLowerCase() })} onPress={onRemove} className="bg-background/85 border-border/50 text-foreground hover:bg-background shadow-sm backdrop-blur-sm">
                  <X aria-hidden="true" className="size-3.5" />
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : onPick ? (
        <button
          type="button"
          onClick={onPick}
          aria-label={t("manualEditor.addTolowercase", { toLowerCase: label.toLowerCase() })}
          className="border-border bg-muted/20 text-muted-foreground hover:border-primary hover:text-primary focus-visible:ring-primary flex h-36 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <ImagePlus aria-hidden="true" className="size-6" />
          {t("manualEditor.addMedia")}
        </button>
      ) : (
        <p className="text-muted-foreground text-xs italic">{t("manualEditor.noMedia")}</p>
      )}
    </div>
  );
}

/** One instruction state — a labelled {@link RichTextEditor}. Uncontrolled after init (TipTap keeps its own doc), so a
 *  reseeded body never yanks the caret; edits bubble via `onChange` to the parent's debounced save. */
function StateEditor({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (html: string) => void;
}) {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <div>
        <span className="text-muted-foreground mb-1 block text-xs font-medium">{label}</span>
        <div
          className="border-border rte-content min-h-[3rem] rounded-lg border px-3 py-2 text-sm"
          // Read-only render of the sanitized HTML (published/non-draft manual) — same markup the reader shows.
          dangerouslySetInnerHTML={{ __html: value || "<p class='text-muted-foreground'>—</p>" }}
        />
      </div>
    );
  }
  return (
    <div>
      <span className="text-muted-foreground mb-1 block text-xs font-medium">{label}</span>
      <RichTextEditor value={value} onChange={onChange} ariaLabel={t("manualEditor.labelText", { label: label })} />
    </div>
  );
}

/** A completed or failed section keeps its own media: a file dropped here must not land on the step's standard media around it. */
function StateDropArea({
  name,
  enabled,
  onDrop,
  children,
}: {
  name: string;
  enabled: boolean;
  onDrop: (name: string, file: DroppedFile) => void;
  children: ReactNode;
}) {
  const { isOver, dropProps } = useSideDrop(
    (drag) => enabled && drag.kind === "file" && fileMatches(drag.name, SETTING_FILE_TYPES[name]),
    (drag) => {
      if (drag.kind === "file") onDrop(name, drag);
    },
    { blockRefused: true },
  );
  return (
    <div {...dropProps} className={cn("rounded-md p-3", isOver && "ring-primary ring-2")}>
      {children}
    </div>
  );
}
